import React, { useState } from 'react';
import {
  Box,
  Typography,
  Snackbar,
  Alert,
  Button,
  Tooltip,
  Chip,
} from '@mui/material';
import { Palette, Close } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';
import DemoChrome from './DemoChrome';
import { getThemeComponent, THEMES } from '../../components/landing-themes/index';
import { MOCK_WEBSITE_LIBRARY, MOCK_WEBSITE_PLANS } from './websiteMockData';
import { GRADIENTS, RADIUS } from '../../theme/tokens';

const ACCENT_SWATCHES = [
  { color: '#F26522', label: 'Brand Orange' },
  { color: '#1B365D', label: 'Navy Blue' },
  { color: '#7C3AED', label: 'Purple' },
  { color: '#0EA5E9', label: 'Sky Blue' },
  { color: '#16A34A', label: 'Forest Green' },
  { color: '#E11D48', label: 'Crimson' },
];

const THEME_KEYS = Object.keys(THEMES);

function LibraryWebsiteDemo() {
  const [themeName, setThemeName] = useState('modern');
  const [accentColor, setAccentColor] = useState('#F26522');
  const [snackOpen, setSnackOpen] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const ThemeComponent = getThemeComponent(themeName);

  const handleBookNow = () => {
    setSnackOpen(true);
  };

  return (
    <DemoChrome title="Library Website">
      {/* Floating control panel toggle */}
      <Box
        sx={{
          position: 'fixed',
          top: 60,
          right: 20,
          zIndex: 1300,
        }}
      >
        <Tooltip title={panelOpen ? 'Close theme picker' : 'Customise theme'} placement="left">
          <Button
            onClick={() => setPanelOpen((v) => !v)}
            aria-label={panelOpen ? 'Close theme picker' : 'Open theme picker'}
            aria-expanded={panelOpen}
            startIcon={panelOpen ? <Close fontSize="small" /> : <Palette fontSize="small" />}
            sx={{
              minWidth: 0,
              height: 44,
              px: 2,
              borderRadius: '999px',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.875rem',
              background: panelOpen ? GRADIENTS.brand : (isDark ? '#17223A' : '#fff'),
              color: panelOpen ? '#fff' : 'text.secondary',
              boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
              '&:hover': {
                background: GRADIENTS.brand,
                color: '#fff',
              },
            }}
          >
            {panelOpen ? 'Close' : 'Theme'}
          </Button>
        </Tooltip>

        {/* Panel */}
        <AnimatePresence>
          {panelOpen && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: -8 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: -8 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              style={{ originX: 1, originY: 0 }}
            >
              <Box
                sx={{
                  mt: 1,
                  p: 2,
                  bgcolor: isDark ? '#17223A' : '#fff',
                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                  borderRadius: `${RADIUS.xl}px`,
                  boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
                  width: 220,
                  maxHeight: 'calc(100vh - 120px)',
                  overflowY: 'auto',
                }}
              >
                {/* Theme switcher */}
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', mb: 1.5 }}
                >
                  Theme
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 2.5 }}>
                  {THEME_KEYS.map((key) => {
                    const meta = THEMES[key];
                    const isSelected = themeName === key;
                    return (
                      <Box
                        key={key}
                        component="button"
                        onClick={() => setThemeName(key)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          border: isSelected
                            ? `2px solid ${accentColor}`
                            : '2px solid transparent',
                          borderRadius: `${RADIUS.md}px`,
                          p: '6px 10px',
                          cursor: 'pointer',
                          bgcolor: isSelected
                            ? `${accentColor}12`
                            : 'transparent',
                          '&:hover': {
                            bgcolor: `${accentColor}08`,
                          },
                          transition: 'all 0.15s',
                          width: '100%',
                          textAlign: 'left',
                        }}
                      >
                        {/* Swatch */}
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: '6px',
                            bgcolor: meta.swatchBg,
                            border: `2px solid ${meta.swatchFg}40`,
                            flexShrink: 0,
                            position: 'relative',
                            overflow: 'hidden',
                          }}
                        >
                          <Box
                            sx={{
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              right: 0,
                              height: '40%',
                              bgcolor: meta.swatchFg,
                              opacity: 0.7,
                            }}
                          />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: isSelected ? 700 : 500,
                              color: isSelected ? accentColor : 'text.primary',
                              display: 'block',
                              lineHeight: 1.2,
                            }}
                          >
                            {meta.name}
                          </Typography>
                        </Box>
                        {isSelected && (
                          <Chip
                            label="Active"
                            size="small"
                            sx={{
                              height: 16,
                              fontSize: '0.55rem',
                              fontWeight: 700,
                              bgcolor: accentColor,
                              color: '#fff',
                            }}
                          />
                        )}
                      </Box>
                    );
                  })}
                </Box>

                {/* Accent color swatches */}
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', mb: 1.5 }}
                >
                  Accent colour
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {ACCENT_SWATCHES.map((s) => (
                    <Tooltip key={s.color} title={s.label} placement="top">
                      <Box
                        component="button"
                        onClick={() => setAccentColor(s.color)}
                        aria-label={`Set accent colour: ${s.label}`}
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          bgcolor: s.color,
                          border: accentColor === s.color
                            ? `3px solid ${isDark ? '#fff' : '#111'}`
                            : '3px solid transparent',
                          cursor: 'pointer',
                          outline: 'none',
                          padding: 0,
                          transition: 'transform 0.15s, border 0.15s',
                          '&:hover': { transform: 'scale(1.15)' },
                          '&:focus-visible': {
                            boxShadow: `0 0 0 2px ${s.color}80`,
                          },
                        }}
                      />
                    </Tooltip>
                  ))}
                </Box>
              </Box>
            </motion.div>
          )}
        </AnimatePresence>
      </Box>

      {/* Rendered theme */}
      <ThemeComponent
        library={MOCK_WEBSITE_LIBRARY}
        plans={MOCK_WEBSITE_PLANS}
        accentColor={accentColor}
        onBookNow={handleBookNow}
      />

      {/* Demo toast */}
      <Snackbar
        open={snackOpen}
        autoHideDuration={4000}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ bottom: { xs: 80, sm: 24 } }}
      >
        <Alert
          onClose={() => setSnackOpen(false)}
          severity="info"
          variant="filled"
          sx={{ fontWeight: 600, borderRadius: `${RADIUS.lg}px` }}
        >
          This is a demo — sign up to enable real bookings.
        </Alert>
      </Snackbar>
    </DemoChrome>
  );
}

export default LibraryWebsiteDemo;
